import { useAuthenticatedUser } from "@/queries/user-queries";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { Text } from "@/components/podkit/typography/Text";
import { Button } from "@/components/flexkit/Button";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { ErrorMessage } from "@/components/ErrorMessage";
import { IconPlusSquare } from "@/assets/icons/geist/IconPlusSquare";
import { IconExternalLink } from "@/assets/icons/geist/IconExternalLink";
import { IconWarningCircleFilled } from "@/assets/icons/geist/IconWarningCircleFilled";
import { Switch } from "@/components/Switch";
import { useDesktop } from "@/hooks/use-desktop";
import { appMainService } from "@/ipc";
import {
    useListIntegrationDefinitions,
    useListIntegrations,
    useCreateIntegration,
    useUpdateIntegration,
    useValidateIntegration,
    integrationValidationQueryKey,
} from "@/queries/integration-queries";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useQueryClient } from "@tanstack/react-query";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Integration, IntegrationDefinition } from "gitpod-next-api/gitpod/v1/integration_pb";
import type { FC } from "react";
import { useState, useCallback } from "react";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";

type IntegrationWithDefinition = {
    integration: PlainMessage<Integration> | null;
    definition: PlainMessage<IntegrationDefinition>;
};

export const Integrations: FC = () => {
    const { data: user } = useAuthenticatedUser();
    const { toast } = useToast();
    const { isDesktop } = useDesktop();

    const {
        data: definitions,
        isLoading: isLoadingDefinitions,
        error: definitionsError,
    } = useListIntegrationDefinitions();

    const {
        data: integrations,
        isLoading: isLoadingIntegrations,
        error: integrationsError,
    } = useListIntegrations(user?.organizationId);

    const api = useGitpodAPI();
    const createIntegrationMutation = useCreateIntegration();
    const updateIntegrationMutation = useUpdateIntegration();

    const [creatingIntegrationId, setCreatingIntegrationId] = useState<string | null>(null);

    const isLoading = isLoadingDefinitions || isLoadingIntegrations;
    const error = definitionsError || integrationsError;

    const integrationsMap = new Map<string, PlainMessage<Integration>>();
    integrations?.forEach((integration) => {
        integrationsMap.set(integration.integrationDefinitionId, integration);
    });

    const integrationsWithStatus =
        definitions?.map((definition) => {
            const integration = integrationsMap.get(definition.id);
            return {
                definition,
                integration: integration || null,
            };
        }) || [];

    const openAuthWindow = useCallback(
        (integrationId: string, definition: PlainMessage<IntegrationDefinition>) => {
            if (!user?.id) return;

            const hasProprietaryApp = definition?.auth?.proprietaryApp !== undefined;
            const hasOAuth = definition?.auth?.oauth !== undefined;
            if (!hasProprietaryApp || !hasOAuth) {
                return;
            }

            // OAuth flow with app_installation flag for org-wide token
            let authUrl = `/integrations/oauth/${integrationId}/authorize?principal=${encodeURIComponent(
                `user/${user.id}`,
            )}&app_installation=true`;

            if (authUrl.startsWith("/")) {
                authUrl = `${window.location.origin}${authUrl}`;
            }

            if (isDesktop && appMainService) {
                void appMainService.openExternal({ url: authUrl });
            } else {
                window.open(authUrl, "_blank", "noopener");
            }

            toast({
                title: "OAuth window opened",
                description:
                    "Complete the authorization in the popup window. Your connection status will update automatically.",
            });
        },
        [isDesktop, toast, user?.id],
    );

    const handleCreateIntegration = useCallback(
        async (definition: PlainMessage<IntegrationDefinition>) => {
            if (!user?.organizationId) return;

            setCreatingIntegrationId(definition.id);

            try {
                const integration = await createIntegrationMutation.mutateAsync({
                    integrationDefinitionId: definition.id,
                    enabled: true,
                    runnerId: "",
                    host: "",
                });
                toast({
                    title: "Integration created successfully",
                    description: `${definition.name} integration has been created and enabled.`,
                });
                // Check if integration requires org-wide app installation (indicated by proprietary_app config)
                if (definition.auth?.proprietaryApp && integration?.id) {
                    openAuthWindow(integration.id, definition);
                }
            } catch (error) {
                toast({
                    title: "Failed to create integration",
                    description: formatError(error),
                });
            } finally {
                setCreatingIntegrationId(null);
            }
        },
        [createIntegrationMutation, openAuthWindow, toast, user?.organizationId],
    );

    const queryClient = useQueryClient();

    const handleToggleEnabled = useCallback(
        async (integration: PlainMessage<Integration>, definition: PlainMessage<IntegrationDefinition>) => {
            const enabling = !integration.enabled;
            try {
                await updateIntegrationMutation.mutateAsync({
                    id: integration.id,
                    enabled: enabling,
                });
                toast({
                    title: "Integration updated successfully",
                    description: `Integration has been ${enabling ? "enabled" : "disabled"}.`,
                });

                if (enabling) {
                    // Validate the integration and trigger OAuth if needed
                    try {
                        const result = await queryClient.fetchQuery({
                            queryKey: integrationValidationQueryKey(integration.id),
                            queryFn: () =>
                                api.integrationService.validateIntegration({
                                    integrationId: integration.id,
                                }),
                            staleTime: 0,
                        });
                        const needsOAuth = result.checks.some((c) => c.actionHint === "app_installation_oauth");
                        if (needsOAuth) {
                            openAuthWindow(integration.id, definition);
                        }
                    } catch {
                        // Validation failed — don't block the enable, the card will show the warning
                    }
                }
            } catch (error) {
                toast({
                    title: "Failed to update integration",
                    description: formatError(error),
                });
            }
        },
        [updateIntegrationMutation, toast, openAuthWindow, queryClient, api],
    );

    return (
        <div className="flex flex-col gap-4">
            <SkeletonBlock ready={!isLoading} className="min-h-[200px]">
                {error && (
                    <div className="flex flex-col gap-2">
                        <Text className="text-lg font-bold text-content-negative">Failed to load integrations</Text>
                        <ErrorMessage error={error} />
                    </div>
                )}

                {!error && (
                    <div className="flex flex-col gap-4">
                        <Text className="text-base text-content-secondary">
                            Manage integrations available to your organization members.{" "}
                            <ExternalLink href="https://ona.com/docs/ona/agents/integrations" iconSize="sm">
                                Learn more.
                            </ExternalLink>
                        </Text>
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                            {integrationsWithStatus.map(({ definition, integration }: IntegrationWithDefinition) => (
                                <IntegrationCard
                                    key={definition.id}
                                    definition={definition}
                                    integration={integration}
                                    onCreateIntegration={() => handleCreateIntegration(definition)}
                                    onToggleEnabled={() => integration && handleToggleEnabled(integration, definition)}
                                    onFixAction={(actionHint) => {
                                        if (actionHint === "app_installation_oauth" && integration) {
                                            void queryClient.invalidateQueries({
                                                queryKey: integrationValidationQueryKey(integration.id),
                                            });
                                            openAuthWindow(integration.id, definition);
                                        }
                                    }}
                                    isCreating={creatingIntegrationId === definition.id}
                                    isUpdating={updateIntegrationMutation.isPending}
                                />
                            ))}
                        </div>

                        {integrationsWithStatus.length === 0 && !isLoading && (
                            <div className="flex flex-col items-center gap-4 py-8">
                                <Text className="text-lg font-bold text-content-secondary">
                                    No integration definitions available
                                </Text>
                                <Text className="text-base text-content-secondary">
                                    Integration definitions will appear here when available.
                                </Text>
                            </div>
                        )}
                    </div>
                )}
            </SkeletonBlock>
        </div>
    );
};

type IntegrationCardProps = {
    definition: PlainMessage<IntegrationDefinition>;
    integration: PlainMessage<Integration> | null;
    onCreateIntegration: () => void;
    onToggleEnabled: () => void;
    onFixAction: (actionHint: string) => void;
    isCreating: boolean;
    isUpdating: boolean;
};

const IntegrationCard: FC<IntegrationCardProps> = ({
    definition,
    integration,
    onCreateIntegration,
    onToggleEnabled,
    onFixAction,
    isCreating,
    isUpdating,
}) => {
    const hasIntegration = !!integration;

    // Validate enabled integrations
    const { data: validation } = useValidateIntegration(
        hasIntegration && integration.enabled ? integration.id : undefined,
    );

    const failingChecks = validation?.checks.filter((c) => c.message) ?? [];

    return (
        <div className="hover:border-border-medium group flex flex-col gap-4 rounded-xl border-0.5 border-solid border-border-base bg-surface-glass px-5 py-3 transition-all duration-200 hover:shadow-md">
            <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                    {definition.iconUrl && (
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg">
                            <img src={definition.iconUrl} alt={`${definition.name} icon`} className="h-8 w-8" />
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        <h3 className="truncate text-lg font-semibold leading-tight text-content-primary">
                            {definition.name}
                        </h3>
                        {definition.description && (
                            <p className="line-clamp-2 text-sm text-content-secondary">{definition.description}</p>
                        )}
                    </div>
                </div>
                {hasIntegration && (
                    <Switch
                        state={integration.enabled ? "checked" : "unchecked"}
                        onToggle={() => onToggleEnabled()}
                        id={`toggle-${definition.id}`}
                        disabled={isUpdating}
                    />
                )}
            </div>

            {failingChecks.length > 0 && (
                <div className="flex flex-col gap-2">
                    {failingChecks.map((check) => (
                        <div
                            key={check.check}
                            className="flex items-center gap-2 rounded-lg bg-surface-warning-subtle px-3 py-2"
                        >
                            <IconWarningCircleFilled size="sm" className="shrink-0 text-content-warning" />
                            <span className="flex-1 text-sm text-content-primary">{check.message}</span>
                            {check.documentationUrl && (
                                <a
                                    href={check.documentationUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex shrink-0 items-center gap-1 text-sm text-content-secondary hover:text-content-primary"
                                >
                                    Learn more
                                    <IconExternalLink size="sm" />
                                </a>
                            )}
                            {check.actionHint && (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    className="shrink-0"
                                    onClick={() => onFixAction(check.actionHint)}
                                    data-tracking-id="fix-integration-check"
                                >
                                    Authorize app
                                </Button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {!hasIntegration && (
                <div className="mt-auto flex items-center justify-end gap-2">
                    <Button
                        variant="primary"
                        onClick={onCreateIntegration}
                        disabled={isCreating}
                        LeadingIcon={IconPlusSquare}
                        data-testid={`create-${definition.id}`}
                        data-tracking-id="oncreateintegration"
                    >
                        {isCreating ? "Creating..." : "Create"}
                    </Button>
                </div>
            )}
        </div>
    );
};
